import { memo, useMemo } from "react";
import styled from "styled-components";
import { InspectionStateEnum } from "./__generated__/InspectionFormOverview.graphql";
import InspectionFormCardActive from "../assets/inspectionformcard_active.svg";
import InspectionFormCardInactive from "../assets/inspectionformcard_inactive.svg";

import Button from "./Buttons/Button";
import { formatDateShort, formateDateTimeShort } from "../utils";

export const InspectionFormCard = memo(function Comp(props: {
  title?: string;
  state: InspectionStateEnum;
  year: number;
  plannedDate?: string;
  assignedDate?: string;
  lastModified?: string;
  onClick: () => void;
}) {
  const isDone = ["APPROVED", "REJECTED", "RETRY"]?.includes(props.state);

  const buttonLabel = useMemo(() => {
    return props.state === "IN_PROGRESS"
      ? "Verder gaan"
      : isDone
      ? "Bekijken"
      : props?.plannedDate
      ? "Starten"
      : "Inplannen";
  }, [isDone, props?.plannedDate, props.state]);

  const buttonDisabled = useMemo(() => {
    if (props.state === "IN_PROGRESS" || isDone || !props?.plannedDate) {
      return false;
    } else {
      return new Date().getFullYear() < props.year;
    }
  }, [isDone, props?.plannedDate, props.state, props.year]);

  return (
    <CardContainer
      bg={!isDone ? InspectionFormCardActive : InspectionFormCardInactive}
    >
      <CardContent>
        <CardYear>
          <CardYearText>{props.year}</CardYearText>
        </CardYear>
        <CardHeader>
          <CardTitle state={props.state} title={props.title}>
            {props.title}
          </CardTitle>
          <CardSubtitle state={props.state}>
            {props?.lastModified
              ? `Laatst bewerkt op ${formateDateTimeShort(props?.lastModified)}`
              : props?.plannedDate
              ? `Ingepland op ${formatDateShort(props?.plannedDate)}`
              : props?.assignedDate
              ? `Toegewezen op ${formateDateTimeShort(props?.assignedDate)}`
              : null}
          </CardSubtitle>
        </CardHeader>
        <CardFooter>
          {props.state ? (
            <Button
              color={isDone ? "white" : "green"}
              label={buttonLabel}
              disabled={buttonDisabled}
              onClick={props.onClick}
            />
          ) : null}
        </CardFooter>
      </CardContent>
    </CardContainer>
  );
});

const InspectionFormCardPlaceholder = () => (
  <CardContainer bg={InspectionFormCardInactive}>
    <CardContent>
      <CardHeader>
        <CardTitle state="REJECTED">Geen formulier gevonden.</CardTitle>
        <CardSubtitle state="REJECTED">
          Er is geen schouwformulier ingesteld voor dit beheerpakket. Neem
          contact op met het collectief.
        </CardSubtitle>
      </CardHeader>
    </CardContent>
  </CardContainer>
);

const CardContainer = styled.div<any>`
  width: 300px;
  height: 217px;
  background-image: url(${(props) => props.bg});
`;

const CardContent = styled.div<any>`
  overflow: hidden;
  height: 160px;
  padding: 26px;
  padding-top: 18px;
  padding-left: 39px;
  padding-bottom: 0px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const CardYear = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 5px;
  max-width: 65px;
  height: 23px;
`;

const CardYearText = styled.span`
  font-family: "Roboto";
  font-style: normal;
  font-weight: 700;
  font-size: 10px;
  line-height: 12px;
  align-items: center;
  align-content: center;
  text-align: center;
  text-transform: uppercase;
`;
const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.span<{
  state: InspectionStateEnum;
}>`
  /* Tablet/H3 */
  font-family: "Roboto";
  font-style: normal;
  font-weight: 700;
  font-size: 18px;

  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2; /* start showing ellipsis when 3rd line is reached */
  white-space: pre-wrap; /* let the text wrap preserving spaces */
  margin-bottom: 9px;

  /* or 22px */
  /* Donkergrijs */
  color: ${({ state }) =>
    !["ASSIGNED", "PLANNED", "IN_PROGRESS"]?.includes(state)
      ? "#212529"
      : "white"};
`;

const CardSubtitle = styled.span<{
  state: InspectionStateEnum;
}>`
  font-family: "Roboto";
  font-style: italic;
  font-weight: 400;
  font-size: 10px;
  line-height: 12px;
  display: flex;
  align-items: center;
  text-transform: uppercase;

  /* Donkergrijs */
  color: ${({ state }) =>
    !["ASSIGNED", "PLANNED", "IN_PROGRESS"]?.includes(state)
      ? "#212529"
      : "white"};
`;

const CardFooter = styled.div``;
export { InspectionFormCardPlaceholder };
export default InspectionFormCard;
